import LFCDialogTitle from '_components/feedback/LFCDialogTitle';
import LFCButton from '_components/inputs/LFCButton';
import ProgressBar from '_components/ProgressBar';
import {getSourceCollectors} from '_logics/LFCSourceUtil';
import {getLFCData, setLFCDataProc} from '_logics/LFCUtil';
import MapPointEditor, {MapIconProps} from 'pages/common/work-images/_components/MapPointEditor';
import MovingList, {ItemRow} from 'pages/common/work-images/_components/MovingList';
import axios, {CancelTokenSource} from 'axios';
import {useSnackbar} from 'notistack';
import {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Slider,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';

const ICON_COLOR = 'rgb(255, 193, 7)';

interface Props {
  open: boolean;
  onClose: (isRefresh: boolean) => void;
  floorId: number;
  floorName: string;
  imageNo: number;
  imagePath: string;
  aspectRatio: number;
  iconRatio: number;
}

const SQL_ID_FLOOR_DEVICES = 8011;

const FloorDeviceDialog = (props: Props) => {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const [startProcess, setStartProcess] = useState(false);
  const [devices, setDevices] = useState<ItemRow[]>([]);
  const [selectDevices, setSelectDevices] = useState<ItemRow[]>([]);
  const [icons, setIcons] = useState<MapIconProps[]>([]);

  const [imgWidth, setImgWidth] = useState(900);
  const [imgHeight, setImgHeight] = useState(0);
  const [maxWidth, setMaxWidth] = useState(900);
  const containerRef = useRef<HTMLDivElement>(null);
  const [iconRatio, setIconRatio] = useState(1);
  const cancelSource = useRef<CancelTokenSource | null>(null);

  const updateWidth = () => {
    if (containerRef.current) {
      const maxW = containerRef.current.offsetWidth - 400;
      setMaxWidth(maxW);
      setImgWidth(maxW * 0.9);
    }
  };

  const onIconChanged = (newIcons: MapIconProps[]) => {
    const newSelectDevices = selectDevices.filter(r => newIcons.some(r2 => r2.id === r.text));
    setSelectDevices(newSelectDevices);
    setIcons(newIcons);
  };

  const onListToEdit = (newSelectedDevices: ItemRow[]) => {
    setSelectDevices(newSelectedDevices);
    const diffDevices = newSelectedDevices.filter(r => !icons.some(r2 => r2.id === r.text));
    setIcons([
      ...icons,
      ...diffDevices.map((r, i) => ({
        id: r.text,
        color: ICON_COLOR,
        position: {relativeX: 2, relativeY: 2 + i}
      }))
    ]);
  };

  const onDelete = (isOk: boolean) => {
    if (!isOk) return;

    if (cancelSource.current) cancelSource.current.cancel('req cancel');

    setStartProcess(true);
    cancelSource.current = axios.CancelToken.source();
    Promise.allSettled([
      setLFCDataProc({
        snack: enqueueSnackbar,
        name: '削除処理',
        invoke_name: 'DeleteFloorImage',
        parameters: {floors: [{floorId: props.floorId, imageNo: props.imageNo}]},
        cancelToken: cancelSource.current.token,
        t
      })
        .then(() => onClose(true))
        .catch(err => {
          console.error(err);
          enqueueSnackbar(t('削除に失敗しました'), {variant: 'error'});
        })
    ]).then(() => setStartProcess(false));
  };

  const onClear = (isOk: boolean) => {
    if (!isOk) return;
    setSelectDevices([]);
    setIcons([]);
  };

  const onSave = () => {
    if (cancelSource.current) cancelSource.current.cancel('req cancel');

    setStartProcess(true);
    cancelSource.current = axios.CancelToken.source();
    Promise.allSettled([
      setLFCDataProc({
        snack: enqueueSnackbar,
        name: '登録処理',
        invoke_name: 'CreateFloorDevices',
        parameters: {
          floorId: props.floorId,
          imageNo: props.imageNo,
          devices: icons.map(r => ({
            device: r.id,
            relative_x: r.position.relativeX,
            relative_y: r.position.relativeY
          })),
          icon_ratio: iconRatio
        },
        cancelToken: cancelSource.current.token,
        t
      })
        .then(() => onClose(true))
        .catch(err => {
          console.error(err);
          enqueueSnackbar(t('登録に失敗しました'), {variant: 'error'});
        })
    ]).then(() => setStartProcess(false));
  };

  const onClose = (isRefresh: boolean) => {
    props.onClose(isRefresh);
  };

  useEffect(() => {
    window.addEventListener('resize', updateWidth);
    return () => {
      window.removeEventListener('resize', updateWidth);
      if (cancelSource.current) cancelSource.current.cancel('req cancel');
    };
  }, []);

  useEffect(() => {
    if (!props.open) {
      setDevices([]);
      setIconRatio(1);
      setSelectDevices([]);
      setIcons([]);
      return;
    }

    setTimeout(updateWidth, 0);
    setDevices([]);
    setIconRatio(props.iconRatio);

    setStartProcess(true);
    cancelSource.current = axios.CancelToken.source();

    getSourceCollectors()
      .then(collectors => {
        const tmp = collectors.map((name, i) => ({idx: i, text: name}));
        if (tmp.length === 0) setStartProcess(false);
        setDevices(tmp);
      })
      .catch(err => {
        console.error(err);
        enqueueSnackbar(t('端末一覧の取得に失敗しました'), {variant: 'error'});
        setStartProcess(false);
      });
  }, [props.open]);

  // 端末リスト取得後に保存済み座標を取得
  useEffect(() => {
    if (!props.open || devices.length === 0) return;

    setStartProcess(true);
    cancelSource.current = axios.CancelToken.source();
    Promise.allSettled([
      getLFCData({
        snack: enqueueSnackbar,
        sql_id: SQL_ID_FLOOR_DEVICES,
        parameters: {floor_id: props.floorId, image_no: props.imageNo},
        cancelToken: cancelSource.current.token,
        t
      }).then((ds: any[]) => {
        if (ds.length === 0) {
          setSelectDevices([]);
          setIcons([]);
          return;
        }

        const savedIcons: MapIconProps[] = ds.map(item => ({
          id: item['device'],
          color: ICON_COLOR,
          position: {relativeX: item['relative_x'], relativeY: item['relative_y']}
        }));

        const newSelectDevices = devices.filter(r => savedIcons.some(r2 => r2.id === r.text));
        setSelectDevices(newSelectDevices);
        setIcons(savedIcons);
      })
    ]).then(() => setStartProcess(false));
  }, [devices]);

  useEffect(() => {
    setImgHeight(imgWidth * props.aspectRatio);
  }, [imgWidth, props.aspectRatio]);

  return (
    <Dialog open={props.open} onClose={() => onClose(false)} fullScreen>
      <ProgressBar startProcess={startProcess} screenLock />
      <LFCDialogTitle onClose={() => onClose(false)}>{t('端末配置')}</LFCDialogTitle>
      <DialogContent ref={containerRef} dividers>
        <Stack direction="row" spacing={1}>
          <Box>
            <MovingList
              title={t('端末')}
              items={devices}
              selectItems={selectDevices}
              onSelectedChanged={onListToEdit}
              width="250px"
              height="80vh"
            />
          </Box>
          <Box width="100%">
            <Grid container spacing={2} mb={2}>
              <Grid item xs={6}>
                <Stack direction="row" spacing={2}>
                  <Box maxWidth="300px">
                    <Chip size="small" label={t('フロア名')} />
                    <Tooltip title={props.floorName} arrow>
                      <Typography mt={0.5} ml={1} noWrap>
                        {props.floorName}
                      </Typography>
                    </Tooltip>
                  </Box>
                  <Box width="100px">
                    <Chip size="small" label={t('画像No')} />
                    <Typography mt={0.5} ml={1}>
                      {props.imageNo}
                    </Typography>
                  </Box>
                  <Box width="200px">
                    <Chip size="small" variant="outlined" label={t('アイコンサイズ')} />
                    <Box ml={1}>
                      <Stack spacing={1} direction="row" sx={{alignItems: 'center'}}>
                        <RemoveIcon />
                        <Slider
                          size="small"
                          valueLabelDisplay="off"
                          value={iconRatio}
                          step={0.1}
                          min={0.5}
                          max={1.5}
                          onChange={(_, newValue) => setIconRatio(newValue as number)}
                        />
                        <AddIcon />
                      </Stack>
                    </Box>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={6}>
                <Stack direction="row" spacing={2}>
                  <Box display="flex" justifyContent="flex-end" width="100%">
                    <Box width="175px" mr={1}>
                      <Stack spacing={1} direction="row" sx={{alignItems: 'center', mb: 1}}>
                        <ZoomOutIcon />
                        <Slider
                          size="small"
                          valueLabelDisplay="off"
                          value={imgWidth}
                          step={50}
                          min={500}
                          max={maxWidth}
                          onChange={(_, newValue) => setImgWidth(newValue as number)}
                        />
                        <ZoomInIcon />
                      </Stack>
                    </Box>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <MapPointEditor
                  containerWidth={imgWidth}
                  containerHeight={imgHeight}
                  imgPath={props.imagePath}
                  icons={icons}
                  iconRatio={iconRatio}
                  onIconChanged={onIconChanged}
                />
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Box display="flex" justifyContent="start" width="100%">
          <LFCButton
            color="secondary"
            onClick={onDelete}
            confirmMessage={t('設定された画像とアイコンを全てクリアします。よろしいですか？')}
          >
            {t('画像削除')}
          </LFCButton>
        </Box>
        <Box display="flex" justifyContent="end" width="100%">
          <LFCButton
            color="secondary"
            onClick={onClear}
            confirmMessage={t('設定されたアイコンを全てクリアします。よろしいですか？')}
          >
            {t('全クリア')}
          </LFCButton>
          <Box pl={5}>
            <LFCButton onClick={() => onClose(false)}>{t('キャンセル')}</LFCButton>
          </Box>
          <Box pl={1}>
            <LFCButton color="primary" onClick={onSave}>
              {t('登録')}
            </LFCButton>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default FloorDeviceDialog;
